// 图片视频轮播组件JavaScript
class ImageVideoSwiper {
  constructor(sectionElement, config) {
    this.section = sectionElement;
    this.config = config;
    this.swiper = null;
    this.videoPlayers = new Map();
    this.init();
  }

  init() {
    this.initSwiper();
    this.initVideoPlayers();
    this.bindEvents();
    this.setupAccessibility();
  }

  initSwiper() {
    const swiperEl = this.section.querySelector('.swiper-main');
    if (!swiperEl) return;

    this.swiper = new Swiper(swiperEl, {
      // 基础配置
      direction: 'horizontal',
      slidesPerView: this.config.slidesPerViewDesktop,
      spaceBetween: this.config.spaceBetween,
      centeredSlides: false,
      grabCursor: true,
      
      // 响应式断点
      breakpoints: {
        320: {
          slidesPerView: this.config.slidesPerViewMobile,
          spaceBetween: 10
        },
        768: {
          slidesPerView: this.config.slidesPerViewMobile,
          spaceBetween: 15
        },
        1024: {
          slidesPerView: this.config.slidesPerViewDesktop,
          spaceBetween: this.config.spaceBetween
        }
      },
      
      // 循环
      loop: this.config.loop,
      
      // 自动播放
      autoplay: this.config.autoplay ? {
        delay: this.config.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      } : false,
      
      // 滚动条
      scrollbar: this.config.showScrollbar ? {
        el: '.swiper-scrollbar',
        draggable: true,
        dragSize: 'auto',
        hide: false
      } : false,
      
      // 分页器
      pagination: this.config.showPagination ? {
        el: '.swiper-pagination',
        clickable: true,
        dynamicBullets: true,
        renderBullet: (index, className) => {
          return `<span class="${className}"></span>`;
        }
      } : false,
      
      // 导航
      navigation: this.config.showNavigation ? {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
        disabledClass: 'swiper-button-disabled'
      } : false,
      
      // 键盘控制
      keyboard: {
        enabled: true,
        onlyInViewport: true
      },
      
      // 鼠标滚轮
      mousewheel: {
        forceToAxis: true,
        sensitivity: 0.5
      },
      
      // 触摸事件
      touchEventsTarget: 'container',
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      
      // 过渡效果
      speed: 500,
      effect: 'slide',
      fadeEffect: {
        crossFade: true
      },
      
      // 观察器
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      
      // 预加载
      preloadImages: true,
      updateOnImagesReady: true,
      lazy: {
        loadPrevNext: true,
        loadPrevNextAmount: 2,
        loadOnTransitionStart: true
      },
      
      // 回调函数
      on: {
        init: () => {
          this.onSwiperInit();
        },
        slideChange: () => {
          this.onSlideChange();
        },
        resize: () => {
          this.onResize();
        },
        touchStart: () => {
          this.pauseAllVideos();
        }
      }
    });
  }

  initVideoPlayers() {
    // 初始化所有视频播放器
    const videoContainers = this.section.querySelectorAll('.video-container');
    
    videoContainers.forEach(container => {
      const videoId = container.dataset.videoId;
      const playButton = container.querySelector('.video-play-button');
      const thumbnail = container.querySelector('.video-thumbnail');
      const videoPlayer = container.querySelector('.video-player');
      
      if (!playButton || !videoPlayer) return;
      
      this.videoPlayers.set(videoId, {
        container,
        playButton,
        thumbnail,
        videoPlayer,
        isPlaying: false,
        iframe: videoPlayer.querySelector('iframe, video')
      });
      
      // 绑定播放按钮点击事件
      playButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.playVideo(videoId);
      });
      
      // 绑定缩略图点击事件
      if (thumbnail) {
        thumbnail.addEventListener('click', (e) => {
          if (e.target === playButton || playButton.contains(e.target)) return;
          this.playVideo(videoId);
        });
      }
      
      // 监听视频播放结束
      const iframe = videoPlayer.querySelector('iframe');
      const videoElement = videoPlayer.querySelector('video');
      
      if (iframe) {
        // 对于iframe，需要监听消息事件
        window.addEventListener('message', (event) => {
          if (event.data === 'videoEnded' || event.data.event === 'ended') {
            this.resetVideo(videoId);
          }
        });
      }
      
      if (videoElement) {
        videoElement.addEventListener('ended', () => {
          this.resetVideo(videoId);
        });
      }
    });
  }

  playVideo(videoId) {
    const videoData = this.videoPlayers.get(videoId);
    if (!videoData) return;
    
    // 暂停所有其他视频
    this.pauseAllVideos(videoId);
    
    // 显示视频播放器，隐藏缩略图
    if (videoData.thumbnail) {
      videoData.thumbnail.style.display = 'none';
    }
    
    videoData.videoPlayer.style.display = 'block';
    videoData.isPlaying = true;
    
    // 开始播放
    const iframe = videoData.iframe;
    const videoElement = videoData.videoPlayer.querySelector('video');
    
    if (iframe) {
      // 对于嵌入视频，重新加载以开始播放
      const src = iframe.src;
      if (!src.includes('autoplay=1')) {
        iframe.src = src.includes('?') ? 
          src.replace('?', '?autoplay=1&') : 
          src + '?autoplay=1';
      }
    }
    
    if (videoElement) {
      videoElement.play().catch(error => {
        console.error('视频播放失败:', error);
        this.resetVideo(videoId);
      });
    }
  }

  pauseAllVideos(exceptVideoId = null) {
    this.videoPlayers.forEach((videoData, videoId) => {
      if (videoId === exceptVideoId) return;
      
      if (videoData.isPlaying) {
        this.resetVideo(videoId);
      }
    });
  }

  resetVideo(videoId) {
    const videoData = this.videoPlayers.get(videoId);
    if (!videoData) return;
    
    // 显示缩略图，隐藏视频播放器
    if (videoData.thumbnail) {
      videoData.thumbnail.style.display = 'block';
    }
    
    videoData.videoPlayer.style.display = 'none';
    videoData.isPlaying = false;
    
    // 重置视频元素
    const iframe = videoData.iframe;
    const videoElement = videoData.videoPlayer.querySelector('video');
    
    if (iframe) {
      // 移除自动播放参数
      const src = iframe.src;
      iframe.src = src.replace(/([?&])autoplay=1(&|$)/, '$1').replace(/\?$/, '');
    }
    
    if (videoElement) {
      videoElement.pause();
      videoElement.currentTime = 0;
    }
  }

  onSwiperInit() {
    console.log('Swiper initialized for section:', this.config.sectionId);
    this.updateAriaLabels();
  }

  onSlideChange() {
    // 幻灯片切换时暂停所有视频
    this.pauseAllVideos();
    this.updateAriaLabels();
  }

  onResize() {
    // 窗口大小改变时重新计算
    setTimeout(() => {
      if (this.swiper) {
        this.swiper.update();
      }
    }, 300);
  }

  bindEvents() {
    // 绑定键盘快捷键
    document.addEventListener('keydown', (e) => {
      if (!this.swiper) return;
      
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.swiper.slidePrev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.swiper.slideNext();
          break;
        case ' ':
          e.preventDefault();
          if (this.swiper.autoplay && this.swiper.autoplay.running) {
            this.swiper.autoplay.stop();
          } else {
            this.swiper.autoplay.start();
          }
          break;
      }
    });
    
    // 绑定窗口大小变化事件
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (this.swiper) {
          this.swiper.update();
        }
      }, 250);
    });
  }

  setupAccessibility() {
    // 设置ARIA标签
    const slides = this.section.querySelectorAll('.swiper-slide');
    const pagination = this.section.querySelector('.swiper-pagination');
    const prevButton = this.section.querySelector('.swiper-button-prev');
    const nextButton = this.section.querySelector('.swiper-button-next');
    
    if (pagination) {
      pagination.setAttribute('role', 'tablist');
      pagination.setAttribute('aria-label', '幻灯片导航');
    }
    
    if (prevButton) {
      prevButton.setAttribute('aria-controls', `swiper-${this.config.sectionId}`);
    }
    
    if (nextButton) {
      nextButton.setAttribute('aria-controls', `swiper-${this.config.sectionId}`);
    }
    
    slides.forEach((slide, index) => {
      slide.setAttribute('role', 'tabpanel');
      slide.setAttribute('aria-roledescription', '幻灯片');
      slide.setAttribute('aria-label', `幻灯片 ${index + 1}，共 ${slides.length} 张`);
    });
  }

  updateAriaLabels() {
    if (!this.swiper) return;
    
    const activeIndex = this.swiper.realIndex;
    const slides = this.swiper.slides;
    
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.setAttribute('aria-hidden', !isActive);
      
      if (isActive) {
        slide.setAttribute('tabindex', '0');
      } else {
        slide.removeAttribute('tabindex');
      }
    });
  }

  // 公共方法
  slideNext() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  slidePrev() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  slideTo(index) {
    if (this.swiper) {
      this.swiper.slideTo(index);
    }
  }

  startAutoplay() {
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.start();
    }
  }

  stopAutoplay() {
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.stop();
    }
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    
    this.videoPlayers.clear();
  }
}

// 导出到全局
window.ImageVideoSwiper = ImageVideoSwiper;

// 自动初始化
document.addEventListener('DOMContentLoaded', function() {
  const sections = document.querySelectorAll('[data-section-type="image-video-swiper"]');
  
  sections.forEach(section => {
    const configScript = section.querySelector('script[type="application/json"]');
    
    if (configScript) {
      try {
        const config = JSON.parse(configScript.textContent);
        new ImageVideoSwiper(section, config);
      } catch (error) {
        console.error('Failed to parse config:', error);
      }
    }
  });
});